import { useEffect, useRef, useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import {
  getReconciliationJobsByProcess,
  getReconciliationJobStatus,
} from "@/Api/reconcialiationFinancialTransactions";
import { Progress } from "@/components/ui/progress";
import { FileText, CheckCircle, XCircle, Clock, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

type JobStatus = {
  uuid: string;
  file_name: string;
  status: "pending" | "processing" | "completed" | "failed";
  progress: number;
  total_records: number | null;
  processed_records: number;
  matched_records: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
};

interface JobsPanelProps {
  processId: string | number;
  onJobsComplete?: () => void;
}

export const JobsPanel = ({ processId, onJobsComplete }: JobsPanelProps) => {
  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const pollingRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const listPollingRef = useRef<NodeJS.Timeout | null>(null);
  const previousActiveJobsRef = useRef<number>(0);

  const stopAllPolling = useCallback(() => {
    pollingRefs.current.forEach((interval) => clearInterval(interval));
    pollingRefs.current.clear();
    if (listPollingRef.current) {
      clearInterval(listPollingRef.current);
      listPollingRef.current = null;
    }
  }, []);

  const stopPolling = useCallback((jobUuid: string) => {
    const interval = pollingRefs.current.get(jobUuid);
    if (interval) {
      clearInterval(interval);
      pollingRefs.current.delete(jobUuid);
    }
  }, []);

  const updateJobStatus = useCallback(
    (uuid: string, updates: Partial<JobStatus>) => {
      setJobs((prev) =>
        prev.map((job) => (job.uuid === uuid ? { ...job, ...updates } : job))
      );
    },
    []
  );

  const pollJobStatus = useCallback(
    async (jobUuid: string) => {
      if (pollingRefs.current.has(jobUuid)) return;

      const interval = setInterval(async () => {
        try {
          const { data, ok } = await getReconciliationJobStatus(jobUuid);

          if (!ok) {
            stopPolling(jobUuid);
            updateJobStatus(jobUuid, {
              status: "failed",
              error_message: "Erro ao buscar status",
            });
            return;
          }

          const status = data?.response;
          updateJobStatus(jobUuid, {
            progress: status.progress,
            total_records: status.total_records,
            processed_records: status.processed_records,
            matched_records: status.matched_records,
            status: status.status,
            error_message: status.error_message,
          });

          if (status.status === "completed" || status.status === "failed") {
            stopPolling(jobUuid);
          }
        } catch (error) {
          console.error("Erro ao buscar status do job:", error);
        }
      }, 2000);

      pollingRefs.current.set(jobUuid, interval);
    },
    [stopPolling, updateJobStatus]
  );

  const fetchJobs = useCallback(async () => {
    if (!processId) return;

    try {
      const { data, ok } = await getReconciliationJobsByProcess(processId);

      if (ok && data?.response) {
        const jobsList: JobStatus[] = data.response;
        setJobs(jobsList);

        jobsList.forEach((job) => {
          if (
            (job.status === "pending" || job.status === "processing") &&
            !pollingRefs.current.has(job.uuid)
          ) {
            pollJobStatus(job.uuid);
          }
        });

        const activeJobs = jobsList.filter(
          (j) => j.status === "pending" || j.status === "processing"
        ).length;

        if (previousActiveJobsRef.current > 0 && activeJobs === 0) {
          onJobsComplete?.();
        }
        previousActiveJobsRef.current = activeJobs;
      }
    } catch (error) {
      console.error("Erro ao buscar jobs:", error);
    } finally {
      setIsLoading(false);
    }
  }, [processId, pollJobStatus, onJobsComplete]);

  useEffect(() => {
    fetchJobs();

    listPollingRef.current = setInterval(fetchJobs, 10000);

    return () => {
      stopAllPolling();
    };
  }, [fetchJobs, stopAllPolling]);

  const getStatusIcon = (status: JobStatus["status"]) => {
    switch (status) {
      case "pending":
        return <Clock className="size-4 text-gray-400" />;
      case "processing":
        return <Loader2 className="size-4 text-blue-500 animate-spin" />;
      case "completed":
        return <CheckCircle className="size-4 text-green-500" />;
      case "failed":
        return <XCircle className="size-4 text-red-500" />;
    }
  };

  const getStatusText = (status: JobStatus["status"]) => {
    switch (status) {
      case "pending":
        return "Aguardando";
      case "processing":
        return "Processando...";
      case "completed":
        return "Concluído";
      case "failed":
        return "Erro";
    }
  };

  const activeJobs = jobs.filter(
    (j) => j.status === "pending" || j.status === "processing"
  );
  const completedJobs = jobs.filter((j) => j.status === "completed");
  const failedJobs = jobs.filter((j) => j.status === "failed");

  if (isLoading) {
    return null;
  }

  if (jobs.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border shadow-sm mb-4">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-t-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <h3 className="font-medium text-sm">Processamento de Conciliação</h3>
          <div className="flex items-center gap-2 text-xs">
            {activeJobs.length > 0 && (
              <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                <Loader2 className="size-3 animate-spin" />
                {activeJobs.length} em andamento
              </span>
            )}
            {completedJobs.length > 0 && (
              <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <CheckCircle className="size-3" />
                {completedJobs.length} concluído(s)
              </span>
            )}
            {failedJobs.length > 0 && (
              <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                <XCircle className="size-3" />
                {failedJobs.length} erro(s)
              </span>
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
          {isExpanded ? (
            <ChevronUp className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </Button>
      </div>

      {isExpanded && (
        <div className="border-t p-3 space-y-2 max-h-[300px] overflow-y-auto">
          {jobs.map((job) => (
            <div
              key={job.uuid}
              className={cn(
                "border rounded-lg p-3 space-y-2",
                job.status === "failed" && "border-red-200 bg-red-50",
                job.status === "completed" && "border-green-200 bg-green-50"
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <FileText className="size-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {job.file_name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(job.status)}
                  <span
                    className={cn(
                      "text-xs",
                      job.status === "failed" && "text-red-600",
                      job.status === "completed" && "text-green-600"
                    )}
                  >
                    {getStatusText(job.status)}
                  </span>
                </div>
              </div>

              {(job.status === "processing" || job.status === "completed") && (
                <div className="space-y-1">
                  <Progress value={job.progress} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{job.progress}%</span>
                    {job.total_records && (
                      <span>
                        {job.processed_records}/{job.total_records}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {job.status === "completed" && (
                <p className="text-xs text-green-600">
                  {job.matched_records} registros conciliados
                </p>
              )}

              {job.status === "failed" && job.error_message && (
                <p className="text-xs text-red-600">{job.error_message}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
